import { useMemo, useState } from 'react';
import { m } from 'framer-motion';
import { getThemesForCategory } from '@brainfart/shared';
import { CategoryChip } from './CategoryChip';
import { RequestTopicLink } from './TopicRequest';
import { ComingSoonTopics } from './ComingSoon';
import { Modal } from './Modal';
import { STARTER_TOPICS } from '../starterTopics';

export interface SetupCategory { key: string; title: string; icon: string; color: string; count: number; }

// Fun, fixed quiz-length "packs" instead of a fiddly stepper. A pack is only
// offered when the selected topics actually hold that many unique
// questions (no silent repeats).
interface Preset { n: number; label: string; emoji: string; }
export const LENGTH_PRESETS: Preset[] = [
  { n: 10, label: 'Quick Fire', emoji: '⚡' },
  { n: 20, label: 'The Standard', emoji: '🎯' },
  { n: 30, label: 'Marathon', emoji: '🔥' },
  { n: 60, label: 'Endurance', emoji: '💀' },
];
export const TIMER_PRESETS: Preset[] = [
  { n: 10, label: 'Blitz', emoji: '🏃' },
  { n: 20, label: 'Normal', emoji: '⏱️' },
  { n: 30, label: 'Chill', emoji: '🧘' },
  { n: 45, label: 'Slowmo', emoji: '🐢' },
];

// Valid length packs given how many unique questions the selection holds.
export function lengthOptionsFor(available: number): Preset[] {
  const opts = LENGTH_PRESETS.filter(p => p.n <= available);
  if (opts.length === 0) opts.push({ n: Math.max(1, available), label: 'Full set', emoji: '🎲' });
  return opts;
}

const MAX_THEMES = 10;

function OptionPill({ preset, active, disabled, onClick, unit = '' }: {
  preset: Preset; active: boolean; disabled?: boolean; onClick: () => void; unit?: string;
}) {
  return (
    <m.button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      whileTap={disabled ? undefined : { scale: 0.96 }}
      style={{
        flex: '1 1 calc(50% - 0.4rem)',
        minWidth: '120px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.15rem',
        padding: '0.9rem 0.5rem',
        borderRadius: '14px',
        border: `2px solid ${active ? '#FFC93C' : '#2E2B45'}`,
        background: active ? 'linear-gradient(135deg, rgba(255,201,60,0.2), rgba(255,159,28,0.08))' : '#15132A',
        color: disabled ? '#555' : active ? '#FFC93C' : '#ccc',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'border-color 0.2s, background 0.2s',
      }}
    >
      <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{preset.emoji}</span>
      <span style={{ fontSize: '0.95rem', fontWeight: 700 }}>{preset.n}{unit}</span>
      <span style={{ fontSize: '0.7rem', color: disabled ? '#555' : '#888' }}>{preset.label}</span>
    </m.button>
  );
}

// Length + timer pickers. Rendered inside the lobby's "set the format" start
// modal, or inside the solo setup's own sheet — same controls either way.
export function LengthTimerSections({ available, rounds, timer, onRounds, onTimer }: {
  available: number; rounds: number; timer: number;
  onRounds: (n: number) => void; onTimer: (n: number) => void;
}) {
  const lockedSome = LENGTH_PRESETS.some(p => p.n > available);
  return (
    <>
      <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#aaa', marginBottom: '0.6rem' }}>How many questions?</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: lockedSome ? '0.5rem' : '1.5rem' }}>
        {LENGTH_PRESETS.map(p => (
          <OptionPill key={p.n} preset={p} active={rounds === p.n} disabled={p.n > available} onClick={() => onRounds(p.n)} />
        ))}
        {available < 10 && (
          <OptionPill preset={{ n: available, label: 'Full set', emoji: '🎲' }} active={rounds === available} onClick={() => onRounds(available)} />
        )}
      </div>
      {lockedSome && (
        <p style={{ fontSize: '0.7rem', color: '#666', marginBottom: '1.5rem' }}>
          🔒 Pick more topics to unlock longer quizzes.
        </p>
      )}

      <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#aaa', marginBottom: '0.6rem' }}>Time per question</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1.5rem' }}>
        {TIMER_PRESETS.map(p => (
          <OptionPill key={p.n} preset={p} active={timer === p.n} onClick={() => onTimer(p.n)} unit="s" />
        ))}
      </div>
    </>
  );
}

function GameOptionsModal({ open, onClose, available, rounds, timer, onRounds, onTimer }: {
  open: boolean; onClose: () => void; available: number; rounds: number; timer: number;
  onRounds: (n: number) => void; onTimer: (n: number) => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title="⚙️ Game options">
      <LengthTimerSections available={available} rounds={rounds} timer={timer} onRounds={onRounds} onTimer={onTimer} />
      <button onClick={onClose} className="btn btn-primary" style={{ width: '100%', padding: '1rem' }}>Done</button>
    </Modal>
  );
}

interface QuizSetupPanelProps {
  categories: SetupCategory[];
  selectedCategories: string[];
  onToggleCategory: (key: string) => void;
  selectedThemes: string[];
  setSelectedThemes: (updater: (prev: string[]) => string[]) => void;
  available: number;
  rounds: number;
  setRounds: (n: number) => void;
  timer: number;
  setTimer: (n: number) => void;
  // When true, the length/timer controls are omitted entirely (the lobby moves
  // them into the "set the format" modal shown on Start). When false/omitted
  // (solo setup), they live behind a compact "⚙️" summary button + sheet.
  hideLengthTimer?: boolean;
}

// The shared category/options/theme picker used by BOTH the multiplayer lobby
// and the solo setup, so the two screens stay identical (only their surrounding
// chrome and start action differ).
export function QuizSetupPanel({
  categories, selectedCategories, onToggleCategory,
  selectedThemes, setSelectedThemes, available, rounds, setRounds, timer, setTimer,
  hideLengthTimer = false,
}: QuizSetupPanelProps) {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [showThemes, setShowThemes] = useState(false);

  // Your picks first, then the usual starters, six at least; the rest behind "Show all".
  const shortList = useMemo(() => {
    const picked = categories.filter(c => selectedCategories.includes(c.key));
    const starters = STARTER_TOPICS.map(k => categories.find(c => c.key === k)).filter((c): c is SetupCategory => Boolean(c) && !selectedCategories.includes(c!.key));
    return [...picked, ...starters].slice(0, Math.max(6, picked.length));
  }, [categories, selectedCategories]);
  const shownTopics = showAllTopics ? categories : shortList;

  const meta = useMemo(() => {
    const byKey: Record<string, SetupCategory> = {};
    categories.forEach(c => { byKey[c.key] = c; });
    return byKey;
  }, [categories]);

  const themesByCategory = useMemo(() => {
    const result: Record<string, Array<{ key: string; theme: string; count: number }>> = {};
    selectedCategories.forEach(catKey => {
      const themes = getThemesForCategory(catKey);
      if (themes.length > 0) {
        result[catKey] = themes.map(t => ({ key: `${catKey}|${t.theme}`, theme: t.theme, count: t.count }));
      }
    });
    return result;
  }, [selectedCategories]);

  const themeCount = selectedThemes.length;

  const toggleExpanded = (catKey: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(catKey)) next.delete(catKey); else next.add(catKey);
    return next;
  });
  const toggleTheme = (themeKey: string) => setSelectedThemes(prev =>
    prev.includes(themeKey) ? prev.filter(t => t !== themeKey) : (prev.length >= MAX_THEMES ? prev : [...prev, themeKey]));
  const addAll = (catKey: string) => {
    const themes = themesByCategory[catKey] || [];
    const toAdd = themes.map(t => t.key).filter(k => !selectedThemes.includes(k));
    const space = MAX_THEMES - selectedThemes.length;
    setSelectedThemes(prev => [...prev, ...toAdd.slice(0, space)]);
  };
  const clearCat = (catKey: string) => {
    const keys = (themesByCategory[catKey] || []).map(t => t.key);
    setSelectedThemes(prev => prev.filter(t => !keys.includes(t)));
  };

  return (
    <div style={{ width: '100%' }}>
      {!hideLengthTimer && (
        /* Solo setup: length + timer summary at the top, opens the options sheet. */
        <button
          onClick={() => setOptionsOpen(true)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.85rem 1.1rem', marginBottom: '1.25rem', borderRadius: '14px',
            border: '1px solid #2E2B45', background: '#1B1830', cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span style={{ fontSize: '1.4rem' }}>⚙️</span>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.7rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Game options</span>
            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>
              🧠 {rounds} questions · ⏱️ {timer}s each
            </span>
          </span>
          <span style={{ marginLeft: 'auto', color: '#FFC93C', fontSize: '0.8rem', fontWeight: 700 }}>Change →</span>
        </button>
      )}

      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ccc', marginBottom: '0.75rem' }}>🎯 Pick topics</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {shownTopics.map(cat => (
          <CategoryChip
            key={cat.key}
            icon={cat.icon}
            title={cat.title}
            color={cat.color}
            active={selectedCategories.includes(cat.key)}
            onClick={() => onToggleCategory(cat.key)}
          />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem', margin: '-0.5rem 0 1rem' }}>
        <button className="text-link" onClick={() => setShowAllTopics(v => !v)}>
          {showAllTopics ? 'Show fewer topics' : `Show all ${categories.length} topics`}
        </button>
        <RequestTopicLink where={hideLengthTimer ? 'lobby' : 'solo_setup'} />
      </div>
      {showAllTopics && <div style={{ marginBottom: '1.25rem' }}><ComingSoonTopics where={hideLengthTimer ? 'lobby' : 'solo_setup'} /></div>}

      {/* An advanced extra: one quiet link until someone wants it (or has already picked themes). */}
      {Object.keys(themesByCategory).length > 0 && !showThemes && selectedThemes.length === 0 && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button className="text-link" onClick={() => setShowThemes(true)}>🎯 Filter by theme (optional)</button>
        </div>
      )}

      {Object.keys(themesByCategory).length > 0 && (showThemes || selectedThemes.length > 0) && (
        <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#aaa' }}>
              🎯 Filter by theme (optional) — {themeCount}/{MAX_THEMES} selected
            </h4>
            {themeCount > 0 && (
              <button onClick={() => setSelectedThemes(() => [])} style={{ fontSize: '0.65rem', color: '#FF6B6B', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem 0.5rem' }}>
                Clear all
              </button>
            )}
          </div>

          {Object.entries(themesByCategory).map(([catKey, themes]) => {
            const isExpanded = expanded.has(catKey);
            const color = meta[catKey]?.color || '#FFC93C';
            const icon = meta[catKey]?.icon || '🎯';
            const title = meta[catKey]?.title || catKey;
            const selectedInCat = themes.filter(t => selectedThemes.includes(t.key));
            const allSelected = selectedInCat.length === themes.length && themes.length > 0;
            const someSelected = selectedInCat.length > 0 && !allSelected;

            return (
              <div key={catKey} style={{ marginBottom: '0.75rem', border: `1px solid ${color}40`, borderRadius: '12px', background: '#1B1830', overflow: 'hidden' }}>
                <button
                  onClick={() => toggleExpanded(catKey)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'none', border: 'none', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
                >
                  <span style={{ fontSize: '1.1rem' }}>{icon}</span>
                  <span style={{ color }}>{title}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.7rem', color: '#888' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: someSelected || allSelected ? color : '#444', border: allSelected ? `2px solid ${color}` : 'none' }} />
                    {selectedInCat.length}/{themes.length}
                    <span style={{ color, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
                  </span>
                </button>

                {isExpanded && (
                  <div style={{ borderTop: `1px solid ${color}20`, padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem' }}>
                      {themes.map(t => {
                        const active = selectedThemes.includes(t.key);
                        const locked = !active && themeCount >= MAX_THEMES;
                        return (
                          <button
                            key={t.key}
                            onClick={() => toggleTheme(t.key)}
                            disabled={locked}
                            style={{
                              padding: '0.4rem 0.75rem', borderRadius: '100px',
                              border: `2px solid ${active ? color : locked ? '#333' : '#2E2B45'}`,
                              background: active ? `${color}30` : locked ? '#15132A' : '#1B1830',
                              color: active ? color : locked ? '#555' : '#ccc',
                              fontSize: '0.7rem', fontWeight: 600, cursor: locked ? 'not-allowed' : 'pointer',
                              opacity: locked ? 0.5 : 1, transition: 'all 0.2s',
                            }}
                          >
                            {t.theme} <span style={{ color: '#666', fontWeight: 400 }}>({t.count})</span>{active && ' ✓'}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => addAll(catKey)}
                        disabled={allSelected || themeCount >= MAX_THEMES}
                        style={{ padding: '0.35rem 0.75rem', borderRadius: '8px', border: `1px solid ${color}`, background: allSelected || themeCount >= MAX_THEMES ? 'transparent' : `${color}20`, color: allSelected || themeCount >= MAX_THEMES ? '#666' : color, fontSize: '0.65rem', fontWeight: 600, cursor: allSelected || themeCount >= MAX_THEMES ? 'not-allowed' : 'pointer' }}
                      >
                        {allSelected ? '✓ All selected' : `+ Add all ${themes.length}`}
                      </button>
                      {selectedInCat.length > 0 && (
                        <button onClick={() => clearCat(catKey)} style={{ padding: '0.35rem 0.75rem', borderRadius: '8px', border: '1px solid #FF6B6B', background: 'transparent', color: '#FF6B6B', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer' }}>
                          Clear ({selectedInCat.length})
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {themeCount >= MAX_THEMES && (
            <p style={{ fontSize: '0.65rem', color: '#FFC93C', marginTop: '0.5rem', textAlign: 'center' }}>
              ⚠️ Maximum {MAX_THEMES} themes reached. Unselect to add more.
            </p>
          )}
          <p style={{ fontSize: '0.65rem', color: '#666', marginTop: '0.5rem', textAlign: 'center' }}>
            {themeCount > 0
              ? `Showing ${themeCount} theme${themeCount > 1 ? 's' : ''} across ${selectedCategories.length} topic${selectedCategories.length > 1 ? 's' : ''}`
              : 'All themes in selected topics'}
          </p>
        </m.div>
      )}

      {!hideLengthTimer && (
        <GameOptionsModal
          open={optionsOpen}
          onClose={() => setOptionsOpen(false)}
          available={available}
          rounds={rounds}
          timer={timer}
          onRounds={setRounds}
          onTimer={setTimer}
        />
      )}
    </div>
  );
}
